<template>
  <!-- Upload an agreement: the AI reads it, the user checks each fact against the text, and Create makes the
       third party and contract (US 9371). Nothing is saved before Create. -->
  <div class="pm-page au-page" qid="agreement-upload">
    <div class="au-head">
      <router-link :to="backTo" class="au-back" qid="agreement-upload-back">
        <font-awesome-icon icon="chevron-left"/>{{ vendor ? vendor.name : $t('agreement_upload.back') }}
      </router-link>
      <h1 class="au-title">{{ step === 'check' ? $t('agreement_upload.check_title') : $t('agreement_upload.title') }}</h1>
      <ol class="api-steps au-steps">
        <li v-for="(name, index) in steps" :key="name" class="api-step" :class="{ 'is-on': index === stepIndex, 'is-done': index < stepIndex }">
          <b><font-awesome-icon v-if="index < stepIndex" icon="check"/><template v-else>{{ index + 1 }}</template></b>{{ $t('agreement_upload.steps.' + name) }}
        </li>
      </ol>
    </div>

    <b-alert v-if="error" show variant="danger" qid="agreement-upload-error">{{ error }}</b-alert>

    <!-- 1. Upload -->
    <section v-if="step === 'upload'" class="v2-card au-card" qid="agreement-upload-pick">
      <p class="au-intro">{{ $t('agreement_upload.upload.intro') }}</p>
      <div v-if="file" class="api-file">
        <span class="api-file__icon"><font-awesome-icon :icon="isWord(file.name) ? 'file-word' : 'file-alt'"/></span>
        <span class="api-file__name">{{ file.name }}<small>{{ fileSize(file.size) }}</small></span>
        <button type="button" class="btn btn-link" qid="agreement-upload-remove" @click="file = null">
          <font-awesome-icon icon="times" class="mr-1"/>{{ $t('agreement_upload.upload.remove') }}
        </button>
      </div>
      <label class="api-drop"
             :class="{ 'is-over': dragOver, 'api-drop--small': file }"
             qid="agreement-upload-drop"
             @dragover.prevent="dragOver = true"
             @dragleave.prevent="dragOver = false"
             @drop.prevent="drop"
      >
        <input type="file" accept=".pdf,.doc,.docx" class="d-none" qid="agreement-upload-input" @change="pick">
        <span class="api-drop__icon"><font-awesome-icon icon="cloud-upload-alt"/></span>
        <span>
          <b>{{ $t(file ? 'agreement_upload.upload.replace' : 'agreement_upload.upload.drop') }}</b>
          <small>{{ $t('agreement_upload.upload.types', { size: maxMb, pages: maxPages }) }}</small>
        </span>
      </label>
      <div class="au-actions">
        <span class="sp-muted">{{ $t('agreement_upload.nothing_saved') }}</span>
        <button type="button" class="v2-ai-btn" :disabled="!file || busy" qid="agreement-upload-read" @click="upload">
          <font-awesome-icon icon="sparkles"/>{{ $t('agreement_upload.upload.read') }}
        </button>
      </div>
    </section>

    <!-- 2a. Reading -->
    <section v-else-if="step === 'reading'" class="v2-card au-card au-reading" qid="agreement-upload-reading">
      <b-spinner small class="mr-2"/>
      <div>
        <b>{{ $t('agreement_upload.reading.title', { file: fileName }) }}</b>
        <p class="sp-muted">{{ $t('agreement_upload.reading.text') }}</p>
      </div>
      <button type="button" class="btn btn-secondary" :disabled="busy" qid="agreement-upload-cancel" @click="discard">{{ $t('agreement_upload.reading.cancel') }}</button>
    </section>

    <!-- 2b. Couldn't be read -->
    <section v-else-if="step === 'failed'" class="v2-card au-card" qid="agreement-upload-failed">
      <b>{{ $t('agreement_upload.failed.title') }}</b>
      <p class="sp-muted">{{ failedText }}</p>
      <button type="button" class="btn btn-secondary" qid="agreement-upload-retry" @click="restart">{{ $t('agreement_upload.failed.retry') }}</button>
    </section>

    <!-- 2c. Check what was found, next to the agreement itself -->
    <div v-else-if="step === 'check' && form" class="au-check">
      <section class="v2-card au-doc" qid="agreement-upload-document">
        <div class="au-doc__head">
          <font-awesome-icon :icon="isWord(fileName) ? 'file-word' : 'file-alt'"/>
          <b>{{ fileName }}</b>
          <span class="sp-muted">{{ $tc('agreement_upload.preview.pages', result.document.pages, { count: result.document.pages }) }}</span>
        </div>
        <iframe v-if="pdfUrl" :key="page" :src="pdfUrl + '#page=' + page" class="au-doc__frame" :title="fileName"></iframe>
        <div v-else class="au-doc__none sp-muted">{{ $t(extraction.has_preview ? 'agreement_upload.preview.loading' : 'agreement_upload.preview.none') }}</div>
      </section>

      <div class="au-facts">
        <div class="v2-card au-found">
          <font-awesome-icon icon="sparkles" class="au-found__icon"/>
          <div>
            <b>{{ $tc('agreement_upload.found', facts.length, { count: facts.length }) }}</b>
            <span v-if="toCheck"> · {{ $tc('agreement_upload.to_check', toCheck, { count: toCheck }) }}</span>
            <p class="sp-muted">{{ $t('agreement_upload.nothing_saved_quotes') }}</p>
          </div>
        </div>

        <!-- Third party -->
        <section class="v2-card sp-sec au-sec" qid="agreement-upload-third-party">
          <div class="sp-sec__head">
            <font-awesome-icon icon="building"/>
            <h3>{{ $t('agreement_upload.sections.third_party') }}</h3>
            <span class="sp-sec__state">{{ $t('agreement_upload.sections.third_party_sub') }}</span>
          </div>
          <div class="au-fact" :class="{ 'is-check': !result.counterparty.sure }">
            <div v-if="existingVendor && form.useExisting" class="au-match">
              <font-awesome-icon icon="link"/>
              <span>{{ $t('agreement_upload.fields.existing') }} <b>{{ existingVendor.name }}</b></span>
              <button type="button" class="btn btn-link" qid="agreement-upload-vendor-new" @click="form.useExisting = false">{{ $t('agreement_upload.fields.use_new') }}</button>
            </div>
            <template v-else>
              <div class="au-grid">
                <label class="au-field au-field--wide">
                  <span class="us-field-label">{{ $t('agreement_upload.fields.name') }} <span class="text-danger">*</span></span>
                  <input v-model="form.vendor.name" class="form-control" qid="agreement-upload-vendor-name">
                </label>
                <label class="au-field">
                  <span class="us-field-label">{{ $t('agreement_upload.fields.company_number') }}</span>
                  <input v-model="form.vendor.company_number" class="form-control" qid="agreement-upload-vendor-number">
                </label>
                <label class="au-field">
                  <span class="us-field-label">{{ $t('agreement_upload.fields.country') }}</span>
                  <b-form-select v-model="form.vendor.country" :options="countryOptions" qid="agreement-upload-vendor-country"/>
                </label>
                <label class="au-field au-field--wide">
                  <span class="us-field-label">{{ $t('agreement_upload.fields.address') }}</span>
                  <input v-model="form.vendor.address" class="form-control" qid="agreement-upload-vendor-address">
                </label>
                <label class="au-field">
                  <span class="us-field-label">{{ $t('agreement_upload.fields.postal_code') }}</span>
                  <input v-model="form.vendor.postal_code" class="form-control" qid="agreement-upload-vendor-postal-code">
                </label>
                <label class="au-field">
                  <span class="us-field-label">{{ $t('agreement_upload.fields.city') }}</span>
                  <input v-model="form.vendor.city" class="form-control" qid="agreement-upload-vendor-city">
                </label>
              </div>
              <button v-if="existingVendor" type="button" class="btn btn-link px-0" qid="agreement-upload-vendor-existing" @click="form.useExisting = true">
                {{ $t('agreement_upload.fields.use_existing', { name: existingVendor.name }) }}
              </button>
            </template>
            <fact-source :fact="result.counterparty" @show="showPage"/>
          </div>
        </section>

        <!-- Contract -->
        <section class="v2-card sp-sec au-sec" qid="agreement-upload-contract">
          <div class="sp-sec__head">
            <font-awesome-icon icon="file-signature"/>
            <h3>{{ $t('agreement_upload.sections.contract') }}</h3>
            <span class="sp-sec__state">{{ $t('agreement_upload.sections.contract_sub') }}</span>
          </div>
          <div class="au-fact">
            <label class="au-field au-field--wide">
              <span class="us-field-label">{{ $t('agreement_upload.fields.contract_name') }} <span class="text-danger">*</span></span>
              <input v-model="form.contract.name" class="form-control" qid="agreement-upload-contract-name">
            </label>
          </div>
          <div class="au-fact" :class="{ 'is-check': !result.role.sure }">
            <label class="au-field">
              <span class="us-field-label">{{ $t('agreement_upload.fields.role') }}</span>
              <b-form-select v-model="form.role" :options="roleOptions" qid="agreement-upload-role"/>
            </label>
            <fact-source :fact="result.role" @show="showPage"/>
          </div>
          <div class="au-fact" :class="{ 'is-check': !result.is_dpa.sure }">
            <b-form-checkbox v-model="form.contract.is_dpa" switch qid="agreement-upload-dpa">{{ $t('agreement_upload.fields.dpa') }}</b-form-checkbox>
            <fact-source :fact="result.is_dpa" @show="showPage"/>
          </div>
          <div class="au-fact" :class="{ 'is-check': !result.effective_date.sure }">
            <label class="au-field">
              <span class="us-field-label">{{ $t('agreement_upload.fields.effective') }}</span>
              <input v-model="form.contract.effective_date" type="date" class="form-control" qid="agreement-upload-effective">
            </label>
            <fact-source :fact="result.effective_date" @show="showPage"/>
          </div>
          <div class="au-fact" :class="{ 'is-check': !result.locations.sure }">
            <span class="us-field-label">{{ $t('agreement_upload.fields.locations') }}</span>
            <country-chips v-model="form.contract.countries" :countries="countriesByName" :options="countryOptions" qid="agreement-upload-locations"/>
            <fact-source :fact="result.locations" @show="showPage"/>
          </div>
          <div class="au-fact">
            <b-form-checkbox v-model="form.owner" switch qid="agreement-upload-owner">{{ $t('agreement_upload.fields.owner') }}</b-form-checkbox>
          </div>
        </section>

        <!-- Sub-processors -->
        <section class="v2-card sp-sec au-sec" qid="agreement-upload-sub-processors">
          <div class="sp-sec__head">
            <font-awesome-icon icon="sitemap"/>
            <h3>{{ $t('agreement_upload.sections.subs') }}</h3>
            <span class="sp-sec__state">{{ $t('agreement_upload.sections.subs_sub') }}</span>
          </div>
          <div v-for="(item, index) in form.subs" :key="'sub' + index" class="au-fact" :class="{ 'is-check': !item.sure, 'is-off': !item.on }">
            <div class="au-row">
              <b-form-checkbox v-model="item.on" :qid="'agreement-upload-sub-' + index"/>
              <b class="au-row__name">{{ item.name }}</b>
              <span class="sp-chip" :class="{ 'is-new': !item.existing }">{{ item.existing ? $t('agreement_upload.subs.existing') : $t('agreement_upload.subs.new') }}</span>
              <span v-if="item.purpose" class="sp-muted">{{ item.purpose }}</span>
            </div>
            <country-chips v-if="item.on" v-model="item.countries" :countries="countriesByName" :options="countryOptions" :qid="'agreement-upload-sub-countries-' + index"/>
            <fact-source :fact="item" @show="showPage"/>
          </div>
          <div v-if="!form.subs.length" class="au-fact sp-muted">{{ $t('agreement_upload.subs.none') }}</div>
          <div v-if="result.sub_processor_page" class="au-fact au-note">
            <font-awesome-icon icon="info-circle"/>{{ $t('agreement_upload.subs.web_list', { url: result.sub_processor_page }) }}
          </div>
        </section>

        <!-- Transfer basis for each country outside the EU/EEA -->
        <section class="v2-card sp-sec au-sec" qid="agreement-upload-bases">
          <div class="sp-sec__head">
            <font-awesome-icon icon="globe"/>
            <h3>{{ $t('agreement_upload.sections.bases') }}</h3>
            <span class="sp-sec__state">{{ $t('agreement_upload.sections.bases_sub') }}</span>
          </div>
          <div v-for="country in outsideCountries" :key="country.name" class="au-fact" :class="{ 'is-check': basisFact(country.name) && !basisFact(country.name).sure, 'is-warn': !form.bases[country.name] }">
            <div class="cp-basis">
              <span class="cp-basis__country"><img v-if="country.flag" :src="country.flag" height="11" class="cp-flag" alt="">{{ country.name }}</span>
              <b-form-select v-model="form.bases[country.name]" :options="basisOptions" class="cp-basis__select" size="sm" :qid="'agreement-upload-basis-' + country.name"/>
            </div>
            <fact-source v-if="basisFact(country.name)" :fact="basisFact(country.name)" @show="showPage"/>
          </div>
          <div v-if="!outsideCountries.length" class="au-fact sp-muted">{{ $t('agreement_upload.bases.none') }}</div>
        </section>

        <!-- Systems it covers -->
        <section v-if="getClient.systemModule" class="v2-card sp-sec au-sec" qid="agreement-upload-systems">
          <div class="sp-sec__head">
            <font-awesome-icon icon="desktop"/>
            <h3>{{ $t('agreement_upload.sections.systems') }}</h3>
            <span class="sp-sec__state">{{ $t('agreement_upload.sections.systems_sub') }}</span>
          </div>
          <div v-for="(item, index) in form.systems" :key="'sys' + item.id" class="au-fact" :class="{ 'is-check': !item.sure, 'is-off': !item.on }">
            <div class="au-row">
              <b-form-checkbox v-model="item.on" :qid="'agreement-upload-system-' + index"/>
              <font-awesome-icon icon="desktop" class="sp-muted"/>
              <b class="au-row__name">{{ item.name }}</b>
              <b-form-select v-model="item.covers" :options="coversOptions" size="sm" class="au-row__select" :disabled="!item.on" :qid="'agreement-upload-system-covers-' + index"/>
            </div>
            <div v-if="!item.sure && item.note" class="au-check-note"><font-awesome-icon icon="exclamation-triangle"/>{{ item.note }}</div>
          </div>
          <div v-if="!form.systems.length" class="au-fact sp-muted">{{ $t('agreement_upload.systems.none') }}</div>
        </section>

        <div class="au-actions au-actions--sticky">
          <button type="button" class="btn btn-secondary" :disabled="busy" qid="agreement-upload-discard" @click="discard">{{ $t('agreement_upload.discard') }}</button>
          <button type="button" class="btn btn-success" :disabled="!canCreate || busy" qid="agreement-upload-create" @click="create">
            <b-spinner v-if="busy" small class="mr-1"/><font-awesome-icon v-else icon="check" class="mr-1"/>{{ form.useExisting && existingVendor ? $t('agreement_upload.create_contract') : $t('agreement_upload.create') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {BASIS_CHOICES} from '@/util/transferBasis.js';

  const POLL_MS = 3000;
  const MAX_MB = 30;
  const MAX_PAGES = 80;
  const ROLES = ['Data processor', 'Data controller', 'Joint controller'];
  const COVERS = ['software_hosting', 'hosting', 'licence', 'support', 'works_in'];

  // Where a fact was found: page and quote (click to show it in the agreement), and what to check
  const FactSource = {
    props: ['fact'],
    render(h) {
      const fact = this.fact || {};
      const parts = [];
      if (fact.quote) {
        parts.push(h('button', {
          class: 'au-quote',
          attrs: { type: 'button', qid: 'agreement-upload-quote' },
          on: { click: () => this.$emit('show', fact.page) }
        }, [
          fact.page ? h('span', { class: 'au-quote__page' }, this.$t('agreement_upload.page', { page: fact.page })) : null,
          '“' + fact.quote + '”'
        ]));
      }
      if (fact.sure === false && fact.note) {
        parts.push(h('div', { class: 'au-check-note' }, [h('font-awesome-icon', { props: { icon: 'exclamation-triangle' } }), fact.note]));
      }
      return parts.length ? h('div', { class: 'au-source' }, parts) : null;
    }
  };

  // Country chips with flags, removable, and a picker to add one
  const CountryChips = {
    props: ['value', 'countries', 'options'],
    render(h) {
      const chips = (this.value || []).map(name => h('span', { class: 'au-country', key: name }, [
        this.countries[name] && this.countries[name].flag ? h('img', { class: 'cp-flag', attrs: { src: this.countries[name].flag, height: 11, alt: '' } }) : null,
        name,
        h('button', {
          class: 'au-country__x',
          attrs: { type: 'button', 'aria-label': this.$t('agreement_upload.upload.remove') },
          on: { click: () => this.$emit('input', this.value.filter(item => item !== name)) }
        }, '×')
      ]));
      const add = h('b-form-select', {
        class: 'au-country__add',
        props: { value: null, size: 'sm', options: [{ value: null, text: this.$t('agreement_upload.fields.add_country') }].concat(this.options.filter(option => option.value && (this.value || []).indexOf(option.value) === -1)) },
        on: { change: name => name && this.$emit('input', (this.value || []).concat([name])) }
      });
      return h('div', { class: 'au-countries' }, chips.concat([add]));
    }
  };

  export default {
    name: 'AgreementUpload',
    components: { FactSource, CountryChips },
    data() {
      return {
        steps: ['upload', 'check', 'create'],
        step: 'upload',
        file: null,
        dragOver: false,
        busy: false,
        error: '',
        extraction: null,
        result: null,
        countries: [],
        form: null,
        pdfUrl: '',
        page: 1,
        poller: null,
        maxMb: MAX_MB,
        maxPages: MAX_PAGES
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      vendor() {
        return this.extraction ? this.extraction.vendor : null;
      },
      backTo() {
        return this.vendor
          ? { name: 'contracts.third_parties.view', params: { slug: this.vendor.slug } }
          : { name: 'contracts.third_parties' };
      },
      stepIndex() {
        return this.step === 'check' ? 1 : 0;
      },
      fileName() {
        return this.extraction && this.extraction.file ? this.extraction.file.name : (this.file ? this.file.name : '');
      },
      failedText() {
        const reason = this.extraction && this.extraction.error;
        const key = 'agreement_upload.failed.' + reason;
        return reason && this.$te(key) ? this.$t(key) : this.$t('agreement_upload.failed.failed');
      },
      // The third party the agreement matches: the one the upload started from, or one found by name
      existingVendor() {
        return (this.result && this.result.counterparty.existing) || this.vendor;
      },
      countriesByName() {
        let byName = {};
        this.countries.forEach(country => { byName[country.name] = country; });
        return byName;
      },
      countryOptions() {
        return [{ value: '', text: '' }].concat(this.countries.map(country => ({ value: country.name, text: country.name })));
      },
      roleOptions() {
        return [{ value: null, text: this.$t('agreement_upload.fields.role_none') }].concat(ROLES.map(role => ({
          value: role, text: this.$t('maintenance.third_parties.processing_role.' + this.$options.filters.slugify(role))
        })));
      },
      basisOptions() {
        return [{ value: null, text: this.$t('vendor_transfer.choose') }].concat(BASIS_CHOICES.map(basis => ({ value: basis, text: this.$t('vendor_transfer.basis.' + basis) })));
      },
      coversOptions() {
        return COVERS.map(covers => ({ value: covers, text: this.$t('systems_overview.covers.' + covers) }));
      },
      // Countries outside the EU/EEA the data goes to, from the contract and the sub-processors that are kept
      outsideCountries() {
        let names = this.form.contract.countries.slice();
        this.form.subs.filter(item => item.on).forEach(item => { names = names.concat(item.countries); });
        return names.filter((name, index) => names.indexOf(name) === index)
          .map(name => this.countriesByName[name])
          .filter(country => country && country.eea === false);
      },
      // Everything the AI filled in, for the count at the top
      facts() {
        if (!this.result) {
          return [];
        }
        const r = this.result;
        return [r.counterparty, r.role, r.is_dpa, r.effective_date, r.locations]
          .filter(fact => fact.quote)
          .concat(r.sub_processors, r.transfer_bases, r.systems);
      },
      toCheck() {
        return this.facts.filter(fact => fact.sure === false).length;
      },
      canCreate() {
        return !!(this.form && this.form.contract.name.trim() && (this.form.useExisting && this.existingVendor || this.form.vendor.name.trim()));
      }
    },
    created() {
      if (this.$route.query.upload) {
        this.load(this.$route.query.upload);
      }
    },
    beforeDestroy() {
      this.stopPolling();
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
      }
    },
    methods: {
      isWord(name) {
        return /\.docx?$/i.test(name || '');
      },
      fileSize(bytes) {
        return bytes > 1048576 ? (bytes / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(bytes / 1024)) + ' KB';
      },
      drop(event) {
        this.dragOver = false;
        this.choose(event.dataTransfer.files[0]);
      },
      pick(event) {
        this.choose(event.target.files[0]);
        event.target.value = '';
      },
      choose(file) {
        this.error = '';
        if (!file) {
          return;
        }
        if (!/\.(pdf|docx?)$/i.test(file.name)) {
          this.error = this.$t('agreement_upload.failed.file_type');
          return;
        }
        if (file.size > MAX_MB * 1048576) {
          this.error = this.$t('agreement_upload.upload.too_big', { size: MAX_MB });
          return;
        }
        this.file = file;
      },
      upload() {
        let formData = new FormData();
        formData.append('client', this.getClient.slug);
        formData.append('file', this.file);
        if (this.$route.query.vendor) {
          formData.append('vendor', this.$route.query.vendor);
        }
        this.busy = true;
        this.error = '';
        piincHttp.post('agreement-extractions', formData).then(response => {
          this.busy = false;
          this.$router.replace({ query: Object.assign({}, this.$route.query, { upload: response.data.id }) });
          this.show(response.data);
        }, error => {
          this.busy = false;
          this.error = this.errorText(error);
        });
      },
      load(id) {
        piincHttp.get('agreement-extractions/' + id).then(response => {
          this.show(response.data);
        }, error => {
          // Gone or someone else's: start over. Other errors are usually a short network problem, so polling tries again.
          if (error.response && error.response.status === 404) {
            this.restart();
          } else if (!this.poller) {
            this.error = this.errorText(error);
          }
        });
      },
      // Shows the step that fits the status
      show(item) {
        this.extraction = item;
        if (item.status === 'ready') {
          this.stopPolling();
          if (!this.result && item.result) {
            this.result = item.result;
            this.countries = item.countries || [];
            this.form = this.formFrom(item.result);
            this.loadPdf();
          }
          this.step = 'check';
        } else if (item.status === 'failed') {
          this.stopPolling();
          this.step = 'failed';
        } else if (item.status === 'pending' || item.status === 'reading') {
          this.step = 'reading';
          this.startPolling();
        } else {
          this.restart();
        }
      },
      // The form starts as the AI left it; doubtful systems start unticked
      formFrom(result) {
        const counterparty = result.counterparty;
        let bases = {};
        result.transfer_bases.forEach(item => { bases[item.country] = item.basis; });
        return {
          useExisting: !!(counterparty.existing || this.vendor),
          vendor: {
            name: counterparty.name || '',
            company_number: counterparty.company_number || '',
            country: counterparty.country || '',
            address: counterparty.address || '',
            postal_code: counterparty.postal_code || '',
            city: counterparty.city || ''
          },
          contract: {
            name: result.contract_name.value || '',
            is_dpa: !!result.is_dpa.value,
            effective_date: result.effective_date.value || '',
            countries: result.locations.countries.slice()
          },
          role: result.role.value || null,
          owner: true,
          subs: result.sub_processors.map(item => Object.assign({}, item, { on: true, countries: item.countries.slice() })),
          bases: bases,
          systems: result.systems.map(item => Object.assign({}, item, { on: item.sure !== false }))
        };
      },
      loadPdf() {
        if (!this.extraction.has_preview) {
          return;
        }
        piincHttp.get('agreement-extractions/' + this.extraction.id + '/file', { responseType: 'blob' }).then(response => {
          this.pdfUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        });
      },
      showPage(page) {
        if (page) {
          this.page = page;
        }
      },
      basisFact(country) {
        return this.result.transfer_bases.find(item => item.country === country);
      },
      startPolling() {
        if (!this.poller) {
          this.poller = setInterval(() => this.extraction && this.load(this.extraction.id), POLL_MS);
        }
      },
      stopPolling() {
        if (this.poller) {
          clearInterval(this.poller);
          this.poller = null;
        }
      },
      restart() {
        this.stopPolling();
        this.extraction = null;
        this.result = null;
        this.form = null;
        this.file = null;
        this.step = 'upload';
        if (this.pdfUrl) {
          URL.revokeObjectURL(this.pdfUrl);
          this.pdfUrl = '';
        }
        if (this.$route.query.upload) {
          let query = Object.assign({}, this.$route.query);
          delete query.upload;
          this.$router.replace({ query: query });
        }
      },
      discard() {
        if (!this.extraction) {
          this.restart();
          return;
        }
        this.busy = true;
        piincHttp.delete('agreement-extractions/' + this.extraction.id).then(() => {
          this.busy = false;
          this.restart();
        }, () => {
          this.busy = false;
          this.restart();
        });
      },
      payload() {
        const form = this.form;
        const outside = this.outsideCountries.map(country => country.name);
        return {
          vendor: form.useExisting && this.existingVendor ? { id: this.existingVendor.id } : Object.assign({}, form.vendor, { country: form.vendor.country || null }),
          contract: {
            name: form.contract.name.trim(),
            is_dpa: form.contract.is_dpa,
            effective_date: form.contract.effective_date || null,
            countries: form.contract.countries
          },
          role: form.role,
          owner: form.owner,
          sub_processors: form.subs.filter(item => item.on).map(item => item.existing
            ? { vendor_id: item.existing.id, countries: item.countries }
            : { name: item.name, countries: item.countries }),
          transfer_bases: outside.filter(name => form.bases[name]).map(name => ({ country: name, basis: form.bases[name] })),
          systems: form.systems.filter(item => item.on).map(item => ({ id: item.id, covers: item.covers }))
        };
      },
      create() {
        this.busy = true;
        this.error = '';
        piincHttp.post('agreement-extractions/' + this.extraction.id + '/save', this.payload()).then(response => {
          this.busy = false;
          const skipped = response.data.skipped_systems.length;
          this.$toastr('success', this.$t('agreement_upload.saved', { name: response.data.contract.name })
            + (skipped ? ' ' + this.$tc('agreement_upload.skipped_systems', skipped, { count: skipped }) : ''));
          this.$router.push({ name: 'contracts.view', params: { slug: response.data.contract.slug } });
        }, error => {
          this.busy = false;
          this.error = this.errorText(error);
        });
      },
      errorText(error) {
        const response = error.response;
        if (response && response.status === 403) {
          return this.$t('agreement_upload.no_access');
        }
        if (response && response.data && response.data.errors) {
          return Object.values(response.data.errors).map(messages => messages[0]).join(' ');
        }
        return this.$t('agreement_upload.failed.failed');
      }
    }
  }
</script>
